import { fmt, type UiStrings } from '@/constants/ui-locale'
import { InfoIcon } from '@/components/Icons'
import type {
  SharedRoomDismissedMatch,
  SharedRoomOfferState,
} from '@/hooks/useSharedRoomOffer'
import { buildSharedMeetingPresentation } from '@/pages/Meetings/utils/sharedRoomPresentation'
import {
  sharedRoomAvatarTone,
  sharedRoomInitials,
  sharedRoomParticipantKey,
} from '@/pages/Meetings/utils/sharedRoomInitials'

type DetectionContext =
  | Extract<SharedRoomOfferState, { kind: 'offered' | 'requested' | 'waiting' }>
  | SharedRoomDismissedMatch

function isLiveOffer(
  ctx: DetectionContext
): ctx is Extract<SharedRoomOfferState, { kind: 'offered' | 'requested' | 'waiting' }> {
  return 'kind' in ctx
}

function SharedRoomUsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 11c1.657 0 3-1.567 3-3.5S17.657 4 16 4s-3 1.567-3 3.5 1.343 3.5 3 3.5zM8 11c1.657 0 3-1.567 3-3.5S9.657 4 8 4 5 5.567 5 7.5 7.343 11 8 11zM8 13c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zM16 13c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
        fill="currentColor"
      />
    </svg>
  )
}

export default function SharedMeetingDetectionModal({
  open,
  context,
  offerState,
  t,
  locale,
  ownMeetingTitle,
  ownUserName,
  meetingCreatedAt,
  meetingSourceLang,
  meetingTargetLang,
  busy,
  onYes,
  onNo,
  onCancel,
  onClose,
}: {
  open: boolean
  context: DetectionContext | null
  offerState: SharedRoomOfferState
  t: UiStrings
  locale: string
  ownMeetingTitle?: string
  ownUserName?: string
  meetingCreatedAt?: string
  meetingSourceLang?: string
  meetingTargetLang?: string
  busy?: boolean
  onYes: () => void
  onNo: () => void
  onCancel: () => void
  onClose: () => void
}) {
  if (!open || !context) return null

  const presentation = buildSharedMeetingPresentation({
    details: {
      peerNames: context.peerNames,
      ...context.meta,
    },
    ownMeetingTitle,
    ownUserName,
    meetingCreatedAt,
    meetingSourceLang,
    meetingTargetLang,
    t,
    locale,
  })

  const isJoiner = isLiveOffer(context) && context.kind === 'requested'
  const isWaiting = isLiveOffer(context) && context.kind === 'waiting'
  const showYesNo = !isJoiner && !isWaiting
  const peerMeetingTitle = presentation.meetingTitle

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shared-meeting-detection-title"
      data-testid="shared-meeting-detection-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-950/40 px-3 py-4 backdrop-blur-sm"
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="animate-scale-in relative w-full max-w-[450px] rounded-2xl border border-slate-100 bg-white p-[25px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
        <div className="flex flex-col gap-3">
          <div className="flex items-start">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-600 dark:border-[var(--border-color)] dark:bg-[var(--accent-light)] dark:text-[var(--accent)]">
              <SharedRoomUsersIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 pl-3.5">
              <h2
                id="shared-meeting-detection-title"
                className="text-base font-bold leading-[22px] tracking-[-0.4px] text-slate-900 dark:text-[var(--text-primary)]"
              >
                {t.sharedRoomDetectionTitle}
              </h2>
              <p className="mt-1 text-xs leading-[18px] text-slate-500 dark:text-[var(--text-secondary)]">
                {peerMeetingTitle ? (
                  <>
                    {t.sharedRoomDetectionBodyBefore}{' '}
                    <span className="font-bold text-slate-500 dark:text-[var(--text-secondary)]">
                      {peerMeetingTitle}
                    </span>{' '}
                    {t.sharedRoomDetectionBodyAfter}
                  </>
                ) : (
                  t.sharedRoomDetectionBodyNoTitle
                )}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200/90 bg-[#fafbfc] p-[15px] dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]">
            <div
              className={`flex items-start gap-2 ${peerMeetingTitle ? 'justify-between' : 'justify-end'}`}
              data-testid="shared-room-detection-meeting-row"
            >
              {peerMeetingTitle ? (
                <p className="min-w-0 text-sm font-bold leading-5 text-slate-900 dark:text-[var(--text-primary)]">
                  {peerMeetingTitle}
                </p>
              ) : null}
              <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                {t.sharedRoomSimilarContentBadge}
              </span>
            </div>
            {presentation.startedLabel ? (
              <p className="mt-1 text-xs text-slate-500 dark:text-[var(--text-secondary)]">
                {fmt(t.sharedRoomDetectionStartedMeta, {
                  started: presentation.startedLabel,
                  languages: presentation.languageLabel,
                })}
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500 dark:text-[var(--text-secondary)]">
                {presentation.languageLabel}
              </p>
            )}

            <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.5px] text-slate-400">
              {fmt(t.sharedRoomParticipantsHeading, { count: presentation.participants.length })}
            </p>
            <ul className="mt-2 space-y-2">
              {presentation.participants.map((member, index) => (
                <li
                  key={sharedRoomParticipantKey(member.name, index)}
                  className="flex items-center gap-2"
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${sharedRoomAvatarTone(member.name)}`}
                  >
                    {sharedRoomInitials(member.name)}
                  </span>
                  <span className="text-sm font-bold text-slate-800 dark:text-[var(--text-primary)]">
                    {member.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-start rounded-xl border border-sky-100 bg-sky-50 px-[15px] py-[11px] dark:border-sky-900/40 dark:bg-sky-950/30">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-800 dark:text-sky-200" />
            <p className="pl-2.5 text-[11.5px] leading-[14px] text-sky-900 dark:text-sky-100">
              {t.sharedRoomDetectionInfo}
            </p>
          </div>

          {isWaiting ? (
            <p className="text-sm text-[var(--text-secondary)]">{t.sharedRoomWaiting}</p>
          ) : null}
          {isJoiner ? (
            <p className="text-sm text-[var(--text-secondary)]">{t.sharedRoomAwaitingApproval}</p>
          ) : null}

          <div className="flex justify-end gap-2.5 pt-2">
            {showYesNo ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={onNo}
                  className="rounded-lg border border-slate-300 bg-white px-[17px] py-[9px] text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-primary)] dark:text-[var(--text-secondary)] dark:hover:bg-[var(--bg-tertiary)]"
                >
                  {t.sharedRoomNo}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={onYes}
                  className="btn-primary rounded-lg px-4 py-2 text-xs font-bold disabled:opacity-50"
                >
                  {t.sharedRoomYes}
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={onCancel}
                className="rounded-lg border border-slate-300 bg-white px-[17px] py-[9px] text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-primary)] dark:text-[var(--text-secondary)] dark:hover:bg-[var(--bg-tertiary)]"
              >
                {t.sharedRoomCancel}
              </button>
            )}
          </div>

          {offerState.kind === 'closed' ? (
            <p className="text-xs text-[var(--text-secondary)]">
              {offerState.reason === 'declined' ? t.sharedRoomDeclined : t.sharedRoomFailed}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
